import React, { useState, useEffect, useCallback } from 'react';
import practiceService from '../../../services/practiceService';
import './HeaderScreens.css';

const EmailNotificationsScreen = ({ onClose }) => {
  const [practices, setPractices] = useState([]);
  const [filteredPractices, setFilteredPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [updateError, setUpdateError] = useState('');

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
  }, []);

  // Filter practices based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPractices(practices);
    } else {
      const filtered = practices.filter(practice =>
        practice.practice_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPractices(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, practices]);

  const fetchPractices = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await practiceService.getPractices({ limit: 1000 });

      let practicesList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
            practicesList = response.data.data.practices;
          } else if (Array.isArray(response.data.data)) {
            practicesList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          practicesList = response.data;
        }
      }

      // Map practices with email notification status
      const practicesWithStatus = practicesList.map((practice, index) => ({
        id: practice.id || index + 1,
        practice_name: practice.practice_name || 'Unnamed Practice',
        // Check if any email notification is enabled (for "Add Patient" notifications)
        // Based on the practice form, these are the fields for email notifications
        email_notifications_enabled: Boolean(
          practice.email_notifications_for_provider ||
          practice.email_notifications_for_superadmin ||
          practice.email_notifications_for_practice_caregiver ||
          practice.email_notifications_for_system_caregiver
        ),
        // Store original values for update
        originalData: {
          email_notifications_for_provider: Boolean(practice.email_notifications_for_provider),
          email_notifications_for_superadmin: Boolean(practice.email_notifications_for_superadmin),
          email_notifications_for_practice_caregiver: Boolean(practice.email_notifications_for_practice_caregiver),
          email_notifications_for_system_caregiver: Boolean(practice.email_notifications_for_system_caregiver)
        }
      }));

      setPractices(practicesWithStatus);
      setFilteredPractices(practicesWithStatus);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotification = async (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    setUpdatingIds(prev => new Set(prev).add(practiceId));
    setUpdateError('');

    try {
      const newStatus = !practice.email_notifications_enabled;

      // Prepare update data - toggle all email notification fields
      const updateData = {
        email_notifications_for_provider: newStatus,
        email_notifications_for_superadmin: newStatus,
        email_notifications_for_practice_caregiver: newStatus,
        email_notifications_for_system_caregiver: newStatus
      };

      const response = await practiceService.updatePractice(practiceId, updateData);

      if (response.data && (response.data.success || response.data)) {
        // Update local state
        setPractices(prevPractices =>
          prevPractices.map(p =>
            p.id === practiceId
              ? {
                ...p,
                email_notifications_enabled: newStatus,
                originalData: {
                  email_notifications_for_provider: newStatus,
                  email_notifications_for_superadmin: newStatus,
                  email_notifications_for_practice_caregiver: newStatus,
                  email_notifications_for_system_caregiver: newStatus
                }
              }
              : p
          )
        );
      } else {
        setUpdateError('Failed to update notification settings. Please try again.');
      }
    } catch (err) {
      console.error('Error updating practice:', err);
      setUpdateError(err.response?.data?.message || 'Failed to update notification settings. Please try again.');
    } finally {
      setUpdatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(practiceId);
        return newSet;
      });
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredPractices.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPractices = filteredPractices.slice(startIndex, endIndex);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="modal-container email-notification-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px' }}>
          <div>
            <h3 className="modal-title" style={{ color: 'var(--color-primary)' }}>Email Notifications Settings</h3>
            <p className="header-screen-subtitle">
              Control practice level notifications for patient addition events.
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            aria-label="Close email notifications panel"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="modal-body" style={{ padding: '20px 20px 10px' }}>
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
              {error}
              <button
                onClick={fetchPractices}
                style={{ marginLeft: '10px', padding: '2px 8px', cursor: 'pointer', border: '1px solid var(--color-danger)', background: 'transparent', color: 'var(--color-danger)', borderRadius: '4px', fontSize: '12px' }}
              >
                Retry
              </button>
            </div>
          )}

          {updateError && (
            <div className="alert alert-danger" style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '13px' }}>
              {updateError}
            </div>
          )}

          <div className="email-notification-toolbar">
            <div className="list-screen-entries-selector">
              <label htmlFor="entriesSelect">Show</label>
              <select
                id="entriesSelect"
                className="list-screen-entries-select"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value={10}>10</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>

            <div className="search-wrapper">
              <label className="search-label" htmlFor="emailSearch">Search:</label>
              <div className="search-container" style={{ margin: 0 }}>
                <input
                  id="emailSearch"
                  className="search-input"
                  type="text"
                  placeholder="Search practices"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="search-icon">
                  <i className="fa fa-search" />
                </span>
              </div>
            </div>
          </div>

          <div className="email-notification-table-wrapper">
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: '20px', marginRight: '10px', color: 'var(--color-primary)' }}></i>
                Loading practices...
              </div>
            ) : filteredPractices.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                {searchTerm ? 'No practices found matching your search.' : 'No practices found.'}
              </div>
            ) : (
              <table className="email-notification-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>#</th>
                    <th>Practice Name</th>
                    <th style={{ width: '240px' }}>Email Notifications Add Patient</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPractices.map((practice, index) => (
                    <tr key={practice.id}>
                      <td>{startIndex + index + 1}</td>
                      <td>{practice.practice_name}</td>
                      <td>
                        <button
                          type="button"
                          className={`status-pill status-${practice.email_notifications_enabled ? 'enabled' : 'disabled'}`}
                          onClick={() => handleToggleNotification(practice.id)}
                          disabled={updatingIds.has(practice.id)}
                          style={{
                            cursor: updatingIds.has(practice.id) ? 'not-allowed' : 'pointer',
                            opacity: updatingIds.has(practice.id) ? 0.6 : 1
                          }}
                        >
                          {updatingIds.has(practice.id) ? (
                            <>
                              <i className="fa fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>
                              Updating...
                            </>
                          ) : (
                            practice.email_notifications_enabled ? 'Enabled' : 'Disabled'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="list-screen-pagination-section" style={{ borderRadius: '0 0 var(--radius-md) var(--radius-md)', margin: '0 -20px -10px', width: 'calc(100% + 40px)', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}>
            <div className="list-screen-pagination-left">
              <span className="ds-pagination-info">
                Showing {filteredPractices.length > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, filteredPractices.length)} of {filteredPractices.length} entries
              </span>
            </div>
            <div className="list-screen-pagination-right">
              <div className="list-screen-page-selector">
                <span>Page</span>
                <select
                  className="list-screen-page-select"
                  value={currentPage}
                  onChange={(e) => setCurrentPage(Number(e.target.value))}
                  disabled={totalPages === 0}
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <option key={page} value={page}>{page}</option>
                  ))}
                </select>
                <span>of {totalPages || 1}</span>
              </div>
              <button
                type="button"
                className="list-screen-option-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || totalPages === 0}
                style={{ padding: '4px 8px', height: '28px' }}
              >
                <i className="fa fa-chevron-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="list-screen-option-btn"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages || totalPages === 0}
                style={{ padding: '4px 8px', height: '28px' }}
              >
                <i className="fa fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailNotificationsScreen;